import { Editable, useEditor, useSlateSelection, ReactEditor, Range } from '../core';
import type { EditableProps } from '../core';
import compose from 'ramda/es/compose';
import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useGamepadBindings } from 'utils/gamepad';
import {
  useWebHIDBindings,
  WebHIDBindingsCallback,
} from 'domains/reporter/WebHID/useWebHIDBindings';
import { measureReportNavigationStart } from '../analytics/performance';
import { usePlugins, useNodeStateRef, useRangeMasksState, PluginsBag } from '../hooks';
import { useRecorder } from 'common/Recorder/useRecorder';
import { logger } from 'modules/logger';
import { useMicrophone } from '../../useMicrophone/useMicrophone';
import { stringifyRange } from '../utils/stringify';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { getHeadingIfCursorRightAtEnd, isLastCharacterAColon } from '../stitching/fragmentHelpers';
import { Node, Transforms } from 'slate';
import { getSurroundingTextString } from '../utils/getSurroundingTextString';
import { insertClipboardDataAsFragment } from '../../Reporter/Fields/utils';
import { GamepadActionId } from 'generated/graphql';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useScrollSelectionIntoView } from '../hooks/useScrollSelectionIntoView';
import { useUnfocusedSelectionHighlight } from '../hooks/useUnfocusedSelectionHighlight';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { useDictaphoneActionMetricBuilder } from './useDictaphoneActionMetricBuilder';

export type BaseEditorProps = EditableProps;

const composeComponents = (
  base: React.ComponentType<BaseEditorProps>,
  enhancers: ReadonlyArray<(...args: any[]) => any>
): React.ComponentType<BaseEditorProps> => {
  if (enhancers.length === 0) {
    return base;
  }
  const allFns = [(props: BaseEditorProps) => createElement(base, props), ...enhancers];
  return (compose as (...fns: Array<(...args: any[]) => any>) => (...args: any[]) => any)(
    ...allFns
  ) as React.ComponentType<BaseEditorProps>;
};

// If keyCode is supplied, it is used for browser events.
// If value is supplied, it is used for event mode (e.g. WebHID).
type OnDictaphoneButtonPress = {
  action: GamepadActionId;
  pressed: boolean;
  keyCode?: string;
  value?: number;
};

export const BaseEditor = (props: BaseEditorProps): React.ReactElement => {
  const { onBlur, onFocus, ...editableProps } = props;
  const {
    decoratePlugins,
    onDOMBeforeInputPlugins,
    onDictaphoneButtonPresses,
    onKeyDownPlugins,
    renderElementPlugins,
    renderLeafPlugins,
    getEditableEnhancers,
  } = usePlugins();
  const editor = useEditor();
  const nodeState = useNodeStateRef();
  const rangeMasks = useRangeMasksState();
  const [isReportContentUnmaskingEnabled] = useFeatureFlagEnabled(FF.REPORT_CONTENT_UNMASKED);
  const { isRecording } = useRecorder();
  const { audioInputDeviceId, audioInputDeviceLabel } = useMicrophone({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { scrollSelectionIntoView } = useScrollSelectionIntoView(editor);
  const selection = useSlateSelection();
  const [isEditorFocused, setIsEditorFocused] = useState(() => ReactEditor.isFocused(editor));
  const { resetUnfocusedSelection, captureUnfocusedSelection, getUnfocusedSelectionDecorations } =
    useUnfocusedSelectionHighlight({ editor, selection, isEditorFocused });

  // Slate's built-in scroll is gated on isFocused, so it silently drops when the editor loses
  // focus mid-dictation (e.g. clicking into the Viewer).
  // This effect re-runs the same scroll logic whenever the selection moves while recording
  // and the editor is unfocused.
  useEffect(() => {
    if (!isRecording || ReactEditor.isFocused(editor)) return;
    if (selection == null) return;
    try {
      const domRange = ReactEditor.toDOMRange(editor, selection);
      scrollSelectionIntoView(editor, domRange);
    } catch {
      // no-op: DOM may not be ready
    }
  }, [editor, isRecording, scrollSelectionIntoView, selection]);

  const buildDictaphoneMetricPayload = useDictaphoneActionMetricBuilder({
    audioInputDeviceId,
    audioInputDeviceLabel,
    isRecording,
  });
  const onDictaphoneButtonPress = useCallback(
    (params: OnDictaphoneButtonPress) => {
      const { action, pressed, keyCode, value } = params;
      if (pressed) {
        const metricPayload =
          value != null
            ? buildDictaphoneMetricPayload({ action, inputMode: 'webhid', webhidValue: value })
            : buildDictaphoneMetricPayload({
                action,
                inputMode: 'gamepad',
                gamepadKeyCode: keyCode,
              });
        const isEventMode = metricPayload.inputMode === 'webhid';

        // Measure navigation start for non-event mode
        if (
          !isEventMode &&
          (action === GamepadActionId.Forward || action === GamepadActionId.Back)
        ) {
          measureReportNavigationStart();
        }

        onDictaphoneButtonPresses({ editor, nodeState })(action);
        analytics.track(reporter.usr.controlInputActionTriggered, metricPayload);

        const message = `[Editable] Dictaphone button press (mode: "${isEventMode ? 'event' : 'browser'}", key: ${isEventMode ? value : keyCode}, action: "${action}") whilst ${isRecording ? '' : 'not '}recording. Cursor now at: "${getSurroundingTextString(
          editor
        )}"`;
        logger.info(message, {
          logId: editor?.selection ? stringifyRange(editor.selection) : '',
          audioInputDeviceLabel,
          selection: editor?.selection ?? '',
        });
      }
    },
    [
      isRecording,
      audioInputDeviceLabel,
      editor,
      onDictaphoneButtonPresses,
      nodeState,
      buildDictaphoneMetricPayload,
    ]
  );
  useGamepadBindings({ callback: onDictaphoneButtonPress, context: APOLLO_QUERY_CONTEXT });
  useWebHIDBindings({ callback: onDictaphoneButtonPress as WebHIDBindingsCallback });

  const decorate = useCallback(
    (...args: Parameters<ReturnType<PluginsBag['decoratePlugins']>>) => {
      const pluginDecorations = decoratePlugins({ editor, nodeState })(...args);
      // If there is a range mask, filter out those decorations so they are hidden
      const visiblePluginDecorations = pluginDecorations.filter((d) => {
        return !rangeMasks.some((rangeMask) => !!Range.intersection(d, rangeMask));
      });
      const visibleUnfocusedSelectionDecorations = getUnfocusedSelectionDecorations(args[0]).filter(
        (d) => !rangeMasks.some((rangeMask) => !!Range.intersection(d, rangeMask))
      );
      return [...visiblePluginDecorations, ...visibleUnfocusedSelectionDecorations];
    },
    [decoratePlugins, editor, nodeState, rangeMasks, getUnfocusedSelectionDecorations]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      onKeyDownPlugins({ editor, nodeState })(event as unknown as KeyboardEvent);

      if (
        event.defaultPrevented === true ||
        event.isDefaultPrevented?.() === true ||
        event.nativeEvent.defaultPrevented === true
      ) {
        event.preventDefault();
      }
    },
    [onKeyDownPlugins, editor, nodeState]
  );

  const onDOMBeforeInput = useCallback(
    (...args: Parameters<ReturnType<PluginsBag['onDOMBeforeInputPlugins']>>) =>
      onDOMBeforeInputPlugins({ editor, nodeState })(...args),
    [onDOMBeforeInputPlugins, editor, nodeState]
  );

  const EnhancedEditable = useMemo(() => {
    const enhancers = getEditableEnhancers();
    return composeComponents(Editable, enhancers) as React.ComponentType<BaseEditorProps>;
  }, [getEditableEnhancers]);

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      // Prevent Slate's built-in paste path so only our custom paste normalization runs.
      event.preventDefault();
      insertClipboardDataAsFragment(editor, event.nativeEvent);
    },
    [editor]
  );

  const onMouseUp = useCallback(
    (_event: React.MouseEvent) => {
      if (editor.selection == null) {
        return;
      }
      /**
       * if user clicks so that it looks like this:
       *
       *  <heading level="1"><text>EXAMINATION:<cursor/></text></heading>
       *  ...
       *
       * move the cursor programmatically to the next inline paragraph
       * (https://sironamedical.atlassian.net/browse/RP-1854)
       */
      const headingEntry = getHeadingIfCursorRightAtEnd(editor, editor.selection);
      if (headingEntry) {
        const headingText = Node.string(headingEntry[0]);
        if (isLastCharacterAColon(headingText)) {
          Transforms.move(editor);
        }
      }
    },
    [editor]
  );

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      setIsEditorFocused(true);
      resetUnfocusedSelection();
      onFocus?.(event);
    },
    [onFocus, resetUnfocusedSelection]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      // On a window blur (e.g. clicking into a separate Viewer window) the editor stays
      // activeElement and keeps its native selection painted.
      // Only treat as a real blur when focus moved to another element in this document first.
      const editorEl = event.currentTarget;
      const activeElement = editorEl?.ownerDocument?.activeElement ?? null;
      const isWindowBlur =
        editorEl != null && (activeElement === editorEl || editorEl.contains(activeElement));
      if (isWindowBlur) {
        onBlur?.(event);
        return;
      }

      setIsEditorFocused(false);
      captureUnfocusedSelection();
      onBlur?.(event);
    },
    [captureUnfocusedSelection, onBlur]
  );

  return (
    <EnhancedEditable
      autoFocus
      decorate={decorate}
      decorations={rangeMasks}
      onDOMBeforeInput={onDOMBeforeInput}
      onKeyDown={onKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPaste={onPaste}
      onMouseUp={onMouseUp}
      renderElement={renderElementPlugins}
      renderLeaf={renderLeafPlugins}
      data-testid="slate-content-editable"
      data-dd-privacy={isReportContentUnmaskingEnabled ? 'allow' : 'mask'}
      {...editableProps}
      // We explicitly dont support drag and drop for now
      onDragStart={(e: React.DragEvent) => {
        e.preventDefault();
      }}
      onDrop={(e: React.DragEvent) => {
        e.preventDefault();
      }}
      style={{
        flex: '1',
        maxWidth: '100%',
      }}
      scrollSelectionIntoView={scrollSelectionIntoView}
    />
  );
};

BaseEditor.displayName = 'BaseEditor';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(BaseEditor);
